import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { TrayImageCapture } from 'components/TrayImageCapture/TrayImageCapture';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useImageUploadManager } from 'hooks/useImageUploadManager';
import useSetDisplayLabel from 'hooks/useSetDisplayLabel';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import Routes, { CHECK_IN_TRAY_ID_PARAM, DELIVERY_ID_PARAM, SET_ID_PARAM } from 'routes';

export const CheckInTrayImageCapture = () => {
  const { deliveryId = '', setId: setKitInstanceId = '', checkInTrayId = '' } = useParams();

  const { data: delivery } = useDelivery(deliveryId);
  const { data: currentUser } = useCurrentUser();
  const [checkInTraysBySet, setCheckInTraysBySet] = useCheckInTraysBySet();
  const { queueImageForUpload } = useImageUploadManager();

  const navigate = useNavigate();

  const deliverySet = useMemo(
    () => delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId),
    [delivery, setKitInstanceId],
  );
  const setName = useSetDisplayLabel(deliverySet);
  const set = checkInTraysBySet[setKitInstanceId];
  const tray = set?.trays[checkInTrayId];
  const trayName = tray?.name || '';

  useEffect(() => {
    // if the tray is not in the checkInTraysBySet, then the user has navigated to the page directly
    // and we should navigate them back to the check in page
    if (!tray) {
      navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));
    }
  }, [deliveryId, navigate, tray]);

  const onBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onImageCapture = useCallback(
    async (imageDataUri: string) => {
      if (!deliveryId || !delivery || !currentUser || !trayName) {
        return;
      }

      const captureId = Date.now().toString();

      // 1. Update the state with the new image object
      setCheckInTraysBySet(prev => {
        const next = { ...prev };
        const trayToUpdate = next[setKitInstanceId]?.trays[checkInTrayId];
        if (trayToUpdate) {
          trayToUpdate.trayImage = {
            captureId,
            dataUri: imageDataUri,
          };
        }
        return next;
      });

      // 2. Immediately queue the upload to run in the background
      queueImageForUpload(
        { stage: 'CHECK_IN', setKitInstanceId, checkInTrayId, captureId, isDecantedImage: false },
        { stage: 'CHECK_IN', deliveryId, setId: setKitInstanceId, trayId: checkInTrayId },
        imageDataUri,
      );

      // 3. Track event and navigate immediately
      await trackEvent(EventType.CaptureTrayImageCheckIn, {
        delivery_id: deliveryId,
        order_id: delivery.orderNumber || '',
        set_id: setKitInstanceId,
        current_user: currentUser.userId || '',
        tenant_name: currentUser.tenantName || '',
        tray_name: trayName,
      });

      navigate(
        Routes.CheckInTrayDetails.path
          .replace(DELIVERY_ID_PARAM, deliveryId)
          .replace(SET_ID_PARAM, setKitInstanceId)
          .replace(CHECK_IN_TRAY_ID_PARAM, checkInTrayId),
      );
    },
    [
      checkInTrayId,
      currentUser,
      delivery,
      deliveryId,
      navigate,
      queueImageForUpload,
      setCheckInTraysBySet,
      setKitInstanceId,
      trayName,
    ],
  );

  return (
    <TrayImageCapture setName={setName} trayName={trayName} onBackClick={onBackClick} onImageCapture={onImageCapture} />
  );
};
